import { StableWebSocket } from 'infrastructure/utility/StableWebSocket';
import { Vehicle } from 'core/models/vehicle';
import { Project } from 'core/models/project';
import { createKeyedListenerManager } from 'utility/KeyedListenerManager';
import { RTCTaskType, RTCTask } from 'core/models/rtc';
import * as tier4_rtc_msgs from './autoware/topics/tier4_rtc_msgs';
import { UUID, normalizeUUID } from '@tier4/autowareviz-messages';

type DriveRTCStatus = {
    vehicle_id: Vehicle.Id,
    payload: tier4_rtc_msgs.CooperateStatusArray;
}

type DriveRTCStatusMessage = {
    type: 'DriveRTCStatus';
    body: DriveRTCStatus;
}

type DriveRTCServiceResponse = {
    vehicle_id: Vehicle.Id;
    transaction_id?: string;
    payload: {
        responses: {
            uuid: { uuid: UUID };
            module: tier4_rtc_msgs.Module;
            success: boolean;
        }[];
    }
}

type DriveRTCServiceResponseMessage = {
    type: 'DriveRTCServiceResponse';
    body: DriveRTCServiceResponse;
}

type DriveRTCMessage = DriveRTCStatusMessage | DriveRTCServiceResponseMessage;

function moduleToTaskType(x: tier4_rtc_msgs.ModuleType): RTCTaskType | undefined {
    switch (x) {
        case 3:
            return 'AVOIDANCE_LEFT';
        case 4:
            return 'AVOIDANCE_RIGHT';
        case 5:
            return 'GOAL_PLANNER';
        case 6:
            return 'START_PLANNER';
        default:
            return undefined;
    }
}

const RTC_STATUS_TIMEOUT = 5 * 1000; // [ms]

export type RTCEventListener = {
    (event: 'task', tasks: RTCTask[]): void;
    (event: 'response', responses: { success: boolean, uuid: string }[], transactionId: string | undefined): void;
}

export class VehicleRTCStatusSubscriber {
    private readonly ws: StableWebSocket;
    private readonly onError: (error: unknown) => void;

    constructor(ws: StableWebSocket, onError: (error: unknown) => void) {
        this.onError = onError;
        this.ws = ws;

        this.setupListeners(ws);
    }

    private readonly statusTable = new Map<Vehicle.Id, Map<string, RTCTask>>();

    private updateStatuses(vehicleId: Vehicle.Id, statuses: tier4_rtc_msgs.CooperateStatus[]) {
        if (this.timeout !== undefined) {
            clearTimeout(this.timeout);
            this.timeout = undefined;
        }

        const existing = this.statusTable.get(vehicleId);
        const updated = existing ?? new Map<string, RTCTask>();
        if (!existing) {
            this.statusTable.set(vehicleId, updated);
        }

        updated.clear();
        statuses.forEach(status => {
            const taskType = moduleToTaskType(status.module.type);
            if (!taskType) return;

            const id = normalizeUUID(status.uuid.uuid);

            updated.set(id, {
                id,
                taskType,
                safe: status.safe,
                auto: status.auto_mode,
                activated: status.command_status.type === 1,
                startDistance: status.start_distance,
                endDistance: status.finish_distance,
            });
        });

        const listeners = this.listenerManager.getListeners(vehicleId) ?? [];
        if (listeners.length > 0) {
            const tasks = [...updated.values()];
            [...listeners].forEach(listener => {
                listener('task', tasks);
            });
        }
    }

    private timeout?: number;

    private startDeadlineTimer(vehicleId: Vehicle.Id) {
        this.timeout = window.setTimeout(() => {
            this.updateStatuses(vehicleId, []);
        }, RTC_STATUS_TIMEOUT);
    }

    private async setupListeners(ws: StableWebSocket) {
        ws.addEventListener('message', msg => {
            const rtcMessage = this.parseMessage(msg);
            if (rtcMessage) {
                if (rtcMessage.type === 'status') {
                    const statuses = rtcMessage;
                    this.updateStatuses(statuses.vehicle_id, statuses.payload.statuses);
                    this.startDeadlineTimer(statuses.vehicle_id);
                }
                if (rtcMessage.type === 'response') {
                    this.listenerManager.forEach(rtcMessage.vehicle_id, listener => {
                        listener('response',
                            rtcMessage.payload.responses.map(res => ({
                                uuid: normalizeUUID(res.uuid.uuid),
                                success: res.success,
                            })),
                            rtcMessage.transaction_id);
                    });
                }
            }
        });

        ws.addEventListener('error', err => {
            console.error('ws for CT status got an error', err);
        });

        ws.addEventListener('close', () => {
            console.warn('ws for CT status is closed');
            const knownProjects = new Set(this.vehicleToProject.values());
            knownProjects.forEach(projectId => {
                this.updateSubscription(projectId);
            });
        });
    }

    private parseMessage(msg: MessageEvent) {
        const json = JSON.parse(msg.data) as DriveRTCMessage;

        switch (json.type) {
            case 'DriveRTCStatus':
                {
                    if (!json.body) {
                        console.warn(json);
                    }
                    return { type: 'status' as const, ...json.body };
                }

            case 'DriveRTCServiceResponse':
                {
                    if (!json.body) {
                        console.warn(json);
                    }
                    return { type: 'response' as const, ...json.body };
                }

            default:
                return undefined;
        }
    }

    private readonly vehicleToProject = new Map<Vehicle.Id, Project.Id>();
    private subscribingVehicles = new Set<Vehicle.Id>();

    private readonly listenerManager = createKeyedListenerManager<Vehicle.Id, RTCEventListener>(vehicleId => {
        const projectId = this.vehicleToProject.get(vehicleId);
        if (!projectId) {
            // something's very wrong
            return () => { /* nothing to do */ };
        }

        if (this.incrementSubscription(vehicleId)) {
            this.updateSubscription(projectId, vehicleId);
        }

        return () => {
            if (this.decrementSubscription(vehicleId)) {
                this.updateSubscription(projectId, vehicleId);
            }
        };
    });

    private incrementSubscription(vehicleId: Vehicle.Id) {
        if (this.subscribingVehicles.has(vehicleId)) return false;

        this.subscribingVehicles.add(vehicleId);
        return true;
    }

    private decrementSubscription(vehicleId: Vehicle.Id) {
        return this.subscribingVehicles.delete(vehicleId);
    }

    private async updateSubscription(projectId: Project.Id, vehicleId?: Vehicle.Id): Promise<void> {
        if (vehicleId === undefined) {
            await Promise.all([...this.subscribingVehicles.keys()].map(v => this.updateSubscription(projectId, v)));
            return;
        }

        if (!this.subscribingVehicles.has(vehicleId)) {
            try {
                this.ws.send(JSON.stringify({
                    'type': 'UnsubscribeVehicleRTCStatusRequest',
                    'body': {
                        'project_id': projectId,
                        'vehicle_id': vehicleId,
                    }
                }));
            } catch (e) {
                this.onError(e);
            }
        } else {
            try {
                this.ws.send(JSON.stringify({
                    'type': 'SubscribeVehicleRTCStatusRequest',
                    'body': {
                        'project_id': projectId,
                        'vehicle_id': vehicleId,
                    }
                }));
            } catch (e) {
                this.onError(e);
            }
        }
    }

    addListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: RTCEventListener): void {
        this.vehicleToProject.set(vehicleId, projectId);
        this.listenerManager.addListener(vehicleId, listener);
    }

    removeListener(vehicleId: Vehicle.Id, _projectId: Project.Id, listener: RTCEventListener): void {
        this.listenerManager.removeListener(vehicleId, listener);
        this.vehicleToProject.delete(vehicleId);
    }
}
